<template>
  <div class="auth-page">
    <section class="d-flex flex-column flex-md-row vh-100 align-items-stretch">
      <!-- Left Side - Image Banner -->
      <div class="d-none d-lg-block w-lg-50 w-xl-60 h-100 bg-primary position-relative">
        <img src="@/assets/images/bg-auth.png" alt="" class="banner-image">
      </div>

      <!-- Right Side - Login Form -->
      <div class="bg-white w-100 w-md-50 w-xl-40 h-100 d-flex align-items-center justify-content-center p-4 p-lg-5">
        <div class="auth-form-container w-100" style="max-width: 520px;">
          <!-- Logo -->
          <div class="mb-2">
            <img src="@/assets/images/logo-auth.png" alt="Eventfy Play" height="50" class="mb-4">
            <h2 class="h4 font-medium text-dark mb-2">Bem-vindo(a) de volta!</h2>
            <p class="text-muted mb-0">Faça login para continuar</p>
          </div>

          <!-- Error Alert -->
          <b-alert
            v-model="isAuthError"
            variant="danger"
            class="mt-3"
            dismissible
          >
            <strong>Ops!</strong> {{ authError }}
          </b-alert>

          <!-- Login Form -->
          <b-form class="mt-4" @submit.prevent="tryToLogIn">
            <!-- Email Input -->
            <b-form-group
              id="input-group-email"
              label="E-mail"
              label-for="input-email"
              class="mb-3"
            >
              <div class="position-relative">
                <i class="mdi mdi-email-outline input-icon"></i>
                <b-form-input
                  id="input-email"
                  v-model="form.email"
                  type="email"
                  placeholder="Insira o seu e-mail"
                  class="custom-input input-with-icon"
                  :class="{ 'is-invalid': submitted && $v.form.email.$error }"
                ></b-form-input>
              </div>
              <div
                v-if="submitted && $v.form.email.$error"
                class="invalid-feedback"
              >
                <span v-if="!$v.form.email.required">E-mail é obrigatório.</span>
                <span v-if="!$v.form.email.email">Por favor, informe e-mail válido.</span>
              </div>
            </b-form-group>

            <!-- Password Input -->
            <b-form-group
              id="input-group-password"
              label="Senha"
              label-for="input-password"
              class="mb-3"
            >
              <div class="position-relative">
                <i class="mdi mdi-lock-outline input-icon"></i>
                <b-form-input
                  id="input-password"
                  v-model="form.password"
                  :type="passwordFieldType"
                  placeholder="******"
                  minlength="6"
                  class="custom-input input-with-icon"
                  :class="{ 'is-invalid': submitted && $v.form.password.$error }"
                ></b-form-input>
                <button
                  type="button"
                  @click="switchVisibility"
                  class="btn btn-link position-absolute password-toggle"
                  tabindex="-1"
                >
                  <i :class="passwordFieldType === 'password' ? 'mdi mdi-eye-outline' : 'mdi mdi-eye-off-outline'" class="text-muted"></i>
                </button>
              </div>
              <div
                v-if="submitted && !$v.form.password.required"
                class="invalid-feedback d-block"
              >
                Senha é obrigatória.
              </div>
            </b-form-group>

            <!-- Remember Me & Forgot Password -->
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div class="custom-control custom-checkbox d-flex align-items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  class="custom-control-input"
                />
                <label class="custom-control-label" for="remember-me">
                  Lembrar-me
                </label>
              </div>
              <a href="#" class="text-muted forgot-password-link">
                Esqueci minha senha
              </a>
            </div>

            <!-- Submit Button -->
            <b-button
              type="submit"
              variant="primary"
              block
              :disabled="tryingToLogIn"
            >
              <span v-if="tryingToLogIn">
                <b-spinner small class="mr-2"></b-spinner>
                AUTENTICANDO...
              </span>
              <span v-else>ENTRAR</span>
            </b-button>
          </b-form>

          <!-- Footer -->
          <div class="text-center mt-5">
            <p class="text-muted mb-0 footer-text">
              © {{ new Date().getFullYear() }} Eventfy Play. Feito com
              <i class="mdi mdi-heart text-danger"></i> por eventfy
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { successMessage } from '@/components/alertsMessages'
import appConfig from '@/app.config'
import { required, email } from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'

export default {
  name: 'Login',
  page: {
    title: 'Login',
    meta: [
      {
        name: 'description',
        content: appConfig.description
      }
    ]
  },
  data () {
    return {
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      passwordFieldType: 'password',
      form: {
        email: '',
        password: ''
      }
    }
  },
  validations: {
    form: {
      email: {
        required,
        email
      },
      password: {
        required
      }
    }
  },
  methods: {
    ...mapActions('auth', ['ActionDoLogin']),
    switchVisibility () {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },
    async tryToLogIn () {
      try {
        this.submitted = true
        this.tryingToLogIn = true
        this.isAuthError = false

        this.$v.form.$touch()

        if (this.$v.form.$invalid) {
          this.tryingToLogIn = false
          return
        }

        await this.ActionDoLogin(this.form)

        let message = 'Sucesso: Login efetuado com sucesso.'
        let delay = 2000

        successMessage(message, delay).then(() => {
          this.$router.push({ name: 'DashboardRealTime' })
        })
      } catch (err) {
        console.log(err)
        this.isAuthError = true
        this.authError = 'Ops, o usuário e/ou senha informados são inválidos. Verifique os parâmetros informados e tente novamente.'
      } finally {
        this.tryingToLogIn = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.auth-page {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.vh-100 {
  height: 100vh;
}

.w-lg-50 {
  @media (min-width: 992px) {
    width: 50% !important;
  }
}

.w-xl-60 {
  @media (min-width: 1200px) {
    width: 60% !important;
  }
}

.w-xl-40 {
  @media (min-width: 1200px) {
    width: 40% !important;
  }
}

// Banner styling
.bg-primary {
  background: linear-gradient(135deg, #1e263d 0%, #27304a 100%);
  position: relative;
  overflow: hidden;
}

.banner-image {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

// Form styling
.auth-form-container {
  animation: fadeInUp 0.5s ease-in-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Password toggle button
.password-toggle {
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.5rem;
  border: none;
  background: transparent;
  z-index: 10;

  &:hover {
    background: transparent;
  }

  &:focus {
    box-shadow: none;
    outline: none;
  }

  i {
    font-size: 1.25rem;
    color: #9e9e9e;
  }
}

// Input styling
.form-control,
.custom-input {
  border-radius: 0.375rem !important;
  border: 1px solid #e0e0e0 !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  font-size: 0.875rem !important;
  padding: 1rem !important;
  height: auto !important;

  &:focus {
    border-color: #1e263d !important;
    box-shadow: 0 2px 8px rgba(30, 38, 61, 0.15) !important;
  }

  &.is-invalid {
    border-color: #f46a6a !important;

    &:focus {
      border-color: #f46a6a !important;
      box-shadow: 0 0 0 0.2rem rgba(244, 106, 106, 0.25) !important;
    }
  }
}

// Input with icon
.input-with-icon {
  padding-left: 2.75rem !important;
}

// Input icon styling
.input-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.125rem;
  color: #9e9e9e;
  z-index: 5;
  pointer-events: none;
  font-weight: 300;
  opacity: 0.7;
}

// Button styling
.btn-primary {
  background: linear-gradient(135deg, #1e263d 0%, #27304a 100%);
  border: none;
  border-radius: 0.375rem;
  transition: all 0.15s ease-in-out;
  padding: 1rem;
  font-size: 0.875rem;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #27304a 0%, #313d5a 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(30, 38, 61, 0.4);
  }

  &:focus {
    background: linear-gradient(135deg, #27304a 0%, #313d5a 100%);
    box-shadow: 0 0 0 0.2rem rgba(30, 38, 61, 0.5);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

// Alert styling
.alert {
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

// Link styling
.text-primary {
  color: #556ee6 !important;

  &:hover {
    color: #4456d4 !important;
  }
}

// Label styling
.form-group {
  margin-bottom: 1.25rem;

  label {
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }
}

// Checkbox styling
.custom-control-input:checked ~ .custom-control-label::before {
  background: linear-gradient(135deg, #1e263d 0%, #27304a 100%) !important;
  border-color: #1e263d !important;
}

.custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: none !important;
  border-color: #ced4da !important;
}

.custom-control-input:focus:checked ~ .custom-control-label::before {
  border-color: #1e263d !important;
}

.custom-control-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 400;
  cursor: pointer;
}

// Forgot password link
.forgot-password-link {
  font-size: 0.875rem;
  text-decoration: none;
  color: #6c757d;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #1e263d;
    text-decoration: none;
  }
}

// Footer text
.footer-text {
  font-size: 12px;
}
</style>
